import { jwtDecode } from "jwt-decode";
// AuthContext.js - Mejorado para asegurar que forceLogout siempre funcione
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

// Crear un evento personalizado para la comunicación entre contextos
export const AUTH_EVENTS = {
  LOGIN: 'auth:login',
  LOGOUT: 'auth:logout',
  FORCE_LOGOUT: 'auth:force_logout'
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        return {
          ...parsedUser,
          id: parsedUser.id || parsedUser._id,
          _id: parsedUser._id || parsedUser.id
        };
      } catch (error) {
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  });
  
  // Referencias para los temporizadores
  const inactivityTimerRef = useRef(null);
  const absoluteTimerRef = useRef(null);
  
  // Flag para controlar si estamos en proceso de logout
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isLoggingOutRef = useRef(false); // Ref para tener acceso más confiable

  // Función para resetear el temporizador de inactividad
  const resetInactivityTimer = () => {
    // Limpiar temporizador existente
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

    // Solo configurar nuevo temporizador si hay un token activo
    if (token) {
      const newTimer = setTimeout(() => {
        console.log('Sesión cerrada por inactividad (25 minutos)');
        logout();
      }, 25 * 60 * 1000); // 25 minutos en milisegundos

      inactivityTimerRef.current = newTimer;
    }
  };

  // Función para iniciar el temporizador de expiración absoluta
  const startAbsoluteTimer = () => {
    // Limpiar temporizador existente
    if (absoluteTimerRef.current) clearTimeout(absoluteTimerRef.current);

    const newTimer = setTimeout(() => {
      console.log('Sesión cerrada por tiempo máximo (8 horas)');
      logout();
    }, 8 * 60 * 60 * 1000); // 8 horas en milisegundos

    absoluteTimerRef.current = newTimer;
  };

  // Configurar detectores de actividad
  const setupActivityListeners = () => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    // Agregar oyentes para todos los eventos
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      // Función de limpieza para remover oyentes
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  };

  // Escuchar actualizaciones del usuario desde UserContext
  useEffect(() => {
    const handleUserUpdated = (event) => {
      const updatedUser = event.detail?.user;
      if (updatedUser) {
        console.log('Actualizando usuario en AuthContext con datos del backend');
        setUser(prevUser => ({
          ...updatedUser,
          id: updatedUser.id || updatedUser._id,
          _id: updatedUser._id || updatedUser.id
        }));
      }
    };
    
    window.addEventListener('user:updated', handleUserUpdated);
    
    return () => {
      window.removeEventListener('user:updated', handleUserUpdated);
    };
  }, []);

  // Función de login mejorada
  const login = (newToken, userData) => {
    // Verificación de seguridad
    if (!userData) {
      console.error('[AUTH] Error de login: userData es null o undefined');
      return;
    }
    if (!newToken) {
      console.error('[AUTH] Error de login: token es null o undefined');
      return;
    }

    try {
      // Asegurar que el usuario tenga un ID
      const userId = userData._id || userData.id;
      if (!userId) {
        console.error('[AUTH] Error de login: usuario sin ID:', userData);
        return;
      }

      // Asegurar que el usuario tenga ambos campos de ID
      const finalUser = {
        ...userData,
        _id: userId,
        id: userId
      };

      console.log('[AUTH] Login exitoso:', { 
        id: userId, 
        token: newToken.substring(0, 10) + '...',
        user: finalUser 
      });

      // Guardar en localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(finalUser));
      localStorage.setItem('loginTime', new Date().getTime());

      // Actualizar el estado
      setToken(newToken);
      setUser(finalUser);
      setIsLoggingOut(false); // Resetear flag de logout
      isLoggingOutRef.current = false;

      // Iniciar temporizadores
      resetInactivityTimer();
      startAbsoluteTimer();
      
      // Emitir evento global con los datos necesarios para el socket
      const loginData = { 
        token: newToken, 
        user: finalUser
      };
      
      // Guardar los datos del login en sessionStorage para asegurar disponibilidad
      sessionStorage.setItem('loginData', JSON.stringify(loginData));
      
      // Emitir evento para notificar a otros componentes
      const loginEvent = new CustomEvent(AUTH_EVENTS.LOGIN, { 
        detail: loginData
      });
      window.dispatchEvent(loginEvent);
      
    } catch (error) {
      console.error('[AUTH] Error durante el login:', error);
    }
  };

  // Mejorado: Función específica para logout forzado desde el servidor
    const forceLogout = useCallback(async (reason = "forced_by_server") => {
    console.log(`[AUTH] Ejecutando forceLogout. Razón: ${reason}`);
    
    try {
      localStorage.clear();
      sessionStorage.clear();
      
      setToken(null);
      setUser(null);
      
      const logoutData = { 
        reason: reason,
        forced: true,
        timestamp: Date.now()
      };
      
      const forceLogoutEvent = new CustomEvent(AUTH_EVENTS.FORCE_LOGOUT, { 
        detail: logoutData
      });
      window.dispatchEvent(forceLogoutEvent);

      window.location.href = '/login?reason=forced';
      
      return true;
    } catch (error) {
      console.error('[AUTH] Error en forceLogout:', error);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login?reason=forced&error=true';
      return false;
    }
  }, []);

    const logout = useCallback(() => {
    if (isLoggingOutRef.current) {
      console.log('[AUTH] Ya hay un proceso de logout en curso, ignorando logout regular');
      return;
    }
    
    setIsLoggingOut(true);
    isLoggingOutRef.current = true;
    
    try {
      const currentUser = {...(user || {})};
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (absoluteTimerRef.current) {
        clearTimeout(absoluteTimerRef.current);
        absoluteTimerRef.current = null;
      }

      const logoutData = { 
        user: currentUser,
        forced: false,
        timestamp: Date.now()
      };
      
      const logoutEvent = new CustomEvent(AUTH_EVENTS.LOGOUT, { 
        detail: logoutData
      });
      window.dispatchEvent(logoutEvent);

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTime');
      sessionStorage.removeItem('loginData');
      
      setToken(null);
      setUser(null);
      
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    } catch (error) {
      console.error('[AUTH] Error durante el logout regular:', error);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }, [user]);

  // Escuchar eventos de logout forzado desde SocketContext
  useEffect(() => {
    const handleSocketForceLogout = (event) => {
      const currentUserId = user?._id || user?.id;
      const targetId = event.detail?.targetId;
      const reason = event.detail?.reason || 'socket_request';

      console.log('[AUTH] Recibido evento socket:force-logout', {
        targetId,
        currentUserId,
        reason,
        timestamp: event.detail?.timestamp,
        socketId: event.detail?.socketId,
        originalData: event.detail?.originalData,
        user
      });

      // Verificar que el evento es para este usuario
      if (!targetId || !currentUserId) {
        console.warn('[AUTH] Logout forzado ignorado: datos incompletos', {
          targetId,
          currentUserId
        });
        return;
      }

      // Verificar si el ID coincide (más flexible, aceptando ambos formatos de ID)
      if (targetId !== currentUserId && targetId !== user?.id) {
        console.warn('[AUTH] Logout forzado ignorado: targetId no coincide con el usuario actual', {
          targetId,
          currentUserId,
          userId: user?.id
        });
        return;
      }

      console.log('[AUTH] Ejecutando forceLogout para usuario:', {
        userId: currentUserId,
        reason,
        timestamp: event.detail?.timestamp
      });

      // Ejecutar forceLogout inmediatamente
      forceLogout(reason);
    };

    // Agregar listener con opción capture para asegurar que se ejecute primero
    window.addEventListener('socket:force-logout', handleSocketForceLogout, { capture: true });

    return () => {
      window.removeEventListener('socket:force-logout', handleSocketForceLogout, { capture: true });
    };
  }, [user]);

  // Efecto para manejar temporizadores al cargar o cambiar token
  useEffect(() => {
    if (token) {
      try {
        // 1. Verificar expiración real del JWT
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp && decoded.exp < currentTime) {
          console.log('Token expirado, cerrando sesión');
          logout();
          return;
        } 

        // 2. Iniciar temporizador de inactividad (25 min)
        resetInactivityTimer();

        // 3. Configurar oyentes de actividad
        const cleanupListeners = setupActivityListeners();

        // 4. Temporizador absoluto basado en la expiración real del token
        const timeUntilExpiry = (decoded.exp - currentTime) * 1000;
        if (timeUntilExpiry > 0) {
          const expiryTimer = setTimeout(() => {
            console.log('Token expiró durante la sesión');
            logout();
          }, timeUntilExpiry);
          absoluteTimerRef.current = expiryTimer; // reutilizamos la misma ref
        }

        // 5. Guardar datos de login en sessionStorage si no existen
        if (user && !sessionStorage.getItem('loginData')) {
          sessionStorage.setItem('loginData', JSON.stringify({ token, user }));
        }

        // Cleanup al desmontar o cambiar token
        return () => {
          if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
          }
          if (absoluteTimerRef.current) {
            clearTimeout(absoluteTimerRef.current);
            absoluteTimerRef.current = null;
          }
          cleanupListeners();
        };
      } catch (error) {
        console.error('Error al validar el token:', error);
        logout();
      }
    }
  }, [token]);

  // NUEVO: Para asegurar que los mensajes de fuerza de cierre de sesión sanos directamente
  useEffect(() => {
    // Comprobación directa cada cierto tiempo
    const checkForceLogoutInterval = setInterval(() => {
      try {
        // Intentar recuperar flags de force-logout de almacenamiento local
        const hasForceLogoutFlag = 
          localStorage.getItem('force_logout_flag') || 
          sessionStorage.getItem('force_logout_flag');
        
        if (hasForceLogoutFlag && user && user._id) {
          console.log('[AUTH] Detectado flag de force-logout en almacenamiento', hasForceLogoutFlag);
          
          // Limpiar flag
          localStorage.removeItem('force_logout_flag');
          sessionStorage.removeItem('force_logout_flag');
          
          // Ejecutar force logout
          forceLogout('storage_flag');
        }
      } catch (error) {
        console.error('[AUTH] Error en intervalo de verificación de force-logout:', error);
      }
    }, 5000); // Cada 5 segundos
    
    return () => {
      clearInterval(checkForceLogoutInterval);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, forceLogout }}>
      {children}
    </AuthContext.Provider>
  ); 
};

export const useAuth = () => useContext(AuthContext);